import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  CheckCircleOutline as CheckIcon,
  Home as HomeIcon,
  Launch as LaunchIcon,
  Publish as PublishIcon,
  Replay as RetryIcon,
} from '@material-ui/icons';
import { ADMIN_RUNTIME_COMMANDS } from '../../../../app/runtime/adminRuntime';
import { useShellAppContext } from '../../../shell/context/useShellAppContext';
import { usePreviewOpener } from '../../../shell/preview/usePreviewOpener';
import { markWizardCompleted } from '../../../shell/session/shellSessionStorage';
import WizardStepLayout from '../../components/common/WizardStepLayout';
import styles from './SummaryStep.module.scss';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

function getRuntimeError(error, fallback) {
  return error?.message || error?.error || fallback;
}

/** Cloud completion adapter for the shared setup wizard. */
const CloudSummaryStep = ({ shopId, onReady }) => {
  const localizeMessage = useLocalizedMessage();
  const history = useHistory();
  const { runtime } = useShellAppContext();
  const { canOpenPreview, openStorefrontPreview } = usePreviewOpener();
  const buildStartedRef = useRef(false);
  const [status, setStatus] = useState('building');
  const [error, setError] = useState(null);
  const [failedOperation, setFailedOperation] = useState(null);

  useEffect(() => {
    if (onReady) onReady(true);
  }, [onReady]);

  const buildPreview = useCallback(async () => {
    setStatus('building');
    setError(null);
    setFailedOperation(null);
    try {
      if (!runtime?.supports(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW)) {
        throw new Error(localizeMessage('wizard.searchSetup.summary.previewUnavailable'));
      }
      const result = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW,
        { shopId },
      );
      if (result?.success === false || result?.status === 'failed') {
        throw new Error(
          result?.error || localizeMessage('wizard.searchSetup.summary.previewFailed'),
        );
      }
      setStatus('preview-ready');
    } catch (nextError) {
      setFailedOperation('preview');
      setError(getRuntimeError(
        nextError,
        localizeMessage('wizard.searchSetup.summary.previewFailed'),
      ));
      setStatus('failed');
    }
  }, [localizeMessage, runtime, shopId]);

  useEffect(() => {
    if (buildStartedRef.current) return;
    buildStartedRef.current = true;
    buildPreview();
  }, [buildPreview]);

  const publish = async () => {
    setStatus('publishing');
    setError(null);
    setFailedOperation(null);
    try {
      if (!runtime?.supports(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE)) {
        throw new Error(localizeMessage('wizard.searchSetup.summary.publishUnavailable'));
      }
      const result = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE,
        { shopId },
      );
      if (result?.success === false || result?.status === 'failed') {
        throw new Error(
          result?.error || localizeMessage('wizard.searchSetup.summary.publishFailed'),
        );
      }
      markWizardCompleted();
      setStatus('published');
    } catch (nextError) {
      setFailedOperation('publish');
      setError(getRuntimeError(
        nextError,
        localizeMessage('wizard.searchSetup.summary.publishFailed'),
      ));
      setStatus('failed');
    }
  };

  const openPreview = async () => {
    try {
      await openStorefrontPreview();
    } catch (nextError) {
      setError(getRuntimeError(
        nextError,
        localizeMessage('wizard.searchSetup.summary.openPreviewFailed'),
      ));
    }
  };

  const goHome = () => history.push(`/app/${shopId}/home`);
  const busy = status === 'building' || status === 'publishing';

  return (
    <WizardStepLayout>
      <Box className={styles.statusCard} data-testid="cloud-summary-step">
        {busy && (
          <>
            <CircularProgress size={60} thickness={4} style={{ marginBottom: 24 }} />
            <Typography variant="h5" className={styles.title}>
              {localizeMessage(status === 'publishing'
                ? 'wizard.searchSetup.summary.publishing.title'
                : 'wizard.searchSetup.summary.building.title')}
            </Typography>
            <Typography variant="body1" className={styles.description}>
              {status === 'publishing'
                ? localizeMessage('wizard.searchSetup.summary.publishing.description')
                : localizeMessage('wizard.searchSetup.summary.building.description')}
            </Typography>
          </>
        )}

        {status === 'failed' && (
          <>
            <Alert severity="error" data-testid="cloud-summary-error">{error}</Alert>
            <Box className={styles.actions}>
              <Button
                startIcon={<RetryIcon />}
                variant="outlined"
                onClick={failedOperation === 'publish' ? publish : buildPreview}
              >
                {localizeMessage(failedOperation === 'publish'
                  ? 'wizard.searchSetup.configuration.retry'
                  : 'wizard.searchSetup.summary.retry')}
              </Button>
            </Box>
          </>
        )}

        {status === 'preview-ready' && (
          <>
            <CheckIcon className={styles.icon} />
            <Typography variant="h4" className={styles.title}>
              {localizeMessage('wizard.searchSetup.summary.previewReady.title')}
            </Typography>
            <Typography variant="body1" className={styles.description}>
              {localizeMessage('wizard.searchSetup.summary.previewReady.description')}
            </Typography>
            <Box className={styles.actions}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<LaunchIcon />}
                onClick={openPreview}
                disabled={!canOpenPreview}
              >
                {localizeMessage('wizard.searchSetup.summary.openPreview')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PublishIcon />}
                onClick={publish}
                data-testid="cloud-publish-button"
              >
                {localizeMessage('wizard.searchSetup.summary.publish')}
              </Button>
            </Box>
          </>
        )}

        {status === 'published' && (
          <>
            <CheckIcon className={styles.icon} />
            <Typography variant="h4" className={styles.title}>
              {localizeMessage('wizard.searchSetup.summary.published.title')}
            </Typography>
            <Typography variant="body1" className={styles.description}>
              {localizeMessage('wizard.searchSetup.summary.published.description')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={goHome}
            >
              {localizeMessage('wizard.searchSetup.summary.goHome')}
            </Button>
          </>
        )}
      </Box>
    </WizardStepLayout>
  );
};

CloudSummaryStep.propTypes = {
  shopId: PropTypes.string.isRequired,
  onReady: PropTypes.func,
};

CloudSummaryStep.defaultProps = {
  onReady: null,
};

export default CloudSummaryStep;
